// @flow
import React, { useContext } from 'react';
import { compose } from 'redux';
import { indexOf, sortBy } from 'lodash';
import classnames from 'classnames';

import { t } from 'Src/i18n';
import ReactAppContext from 'Context/ReactAppContext';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { LINE_COMMON_LAST_SEEN_PRODUCTS } from 'Utils/request/requestDictionary';
import resortThumbnailQuery from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { getLastVisitedProductIds } from 'Tracking/last-visited-tracking-api';
import ReactResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { getProductProps, setProductHref } from 'Components/widgets/product/Product.mapper';
import * as configQueries from 'Views/configQueries';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import Grid from 'Components/frameworks/grid/Grid';
import { LineTitle } from 'Components/widgets/line-title/LineTitle';
import { Product } from 'Components/widgets/product/Product';
import { Slider } from 'Components/frameworks/slider/Slider';
import { TrackClick } from 'Tracking/track-click/TrackClick';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';

type CMSProps = {
  id: string,
  title: string,
  seeMoreResortsLabel: string,
};

type GQLProps = {
  config: {
    common: {
      next: string,
      previous: string,
      close: string,
    },
    price: CMSPrice,
    resort: CMSResort,
  },
};

type Props = CMSProps & GQLProps;

const MAX_LAST_SEEN_PRODUCTS = 3;

function lastSeenProductsQuery(appContext: AppContext, productIds: $ReadOnlyArray<string>) {
  const { cmsContent = '', cmsRelease = '' } = appContext;
  const cmsArgs = `content: "${cmsContent}", release: "${cmsRelease}"`;

  const resortThumbnailQueryString = resortThumbnailQuery({
    content: cmsContent,
    release: cmsRelease,
  });
  const query = `query {
      products: resorts(resortIds : ${JSON.stringify(productIds)}) {
        ${resortThumbnailQueryString}
      },
      config(${cmsArgs}) {
        common {
          next
          previous
          close
          thumbnailCTA
          thumbnailTourCTA
        }
        ${configQueries.price()}
        ${configQueries.resort(appContext)}
      }
    }`;
  return query;
}

// Placeholder styling replicates Product card image aspect ratio
const LastSeenPlaceholder = ({ className = '', styles = {} }) => (
  <div className={className} style={styles}>
    <div
      className="relative"
      style={{
        height: '0',
        paddingBottom: 'calc(240 / 380 * 100%)',
      }}
    >
      <div className="absolute pin h-full w-full border-2 border-dashed" />
    </div>
  </div>
);

export const LastSeenProductsLine = ({ id, title, seeMoreResortsLabel }: Props) => {
  const appContext = useContext(ReactAppContext);
  const { isAppMounted, isDesktop } = useContext(ReactResponsiveContext);
  const track = useTracking({
    actionGA: 'lastSeenProducts',
  });

  // Get last seen products from localStorage
  const productIds = getLastVisitedProductIds().slice(0, MAX_LAST_SEEN_PRODUCTS);

  const { data, isLoading } = useGQL({
    query: lastSeenProductsQuery(appContext, productIds),
    queryOptions: {
      requestName: LINE_COMMON_LAST_SEEN_PRODUCTS,
    },
    ignore: productIds.length <= 0,
    cache: true,
  });

  if (!isAppMounted) {
    return null;
  }

  let lastSeenProducts = [];

  if (!isLoading && data && data.products) {
    lastSeenProducts = sortBy(
      data.products.map(product =>
        compose(
          getProductProps,
          setProductHref,
        )({ config: data.config, product, isSliderDisabled: true }),
      ),
      product => indexOf(productIds, product.id),
    );
  }

  const numberOfProducts = lastSeenProducts.length;
  const numberOfPlaceholders = MAX_LAST_SEEN_PRODUCTS - numberOfProducts;

  return (
    <SiteContainer className="Line" id={id}>
      {numberOfProducts > 0 &&
        title &&
        seeMoreResortsLabel && (
          <div className="flex flex-wrap justify-between">
            <LineTitle className="flex-1 md:flex-none">{title}</LineTitle>
            <TrackClick
              as={DynamicLink}
              className="m-auto md:mx-0 mt-4 md:mt-1 order-last md:order-none text-base text-gray-darker hover:text-gray-active focus:text-gray-active underline"
              trackingData={{
                actionGA: 'lastSeenProducts',
                labelGA: 'seeMore',
              }}
              url="/s"
            >
              {seeMoreResortsLabel}
            </TrackClick>

            <div className="w-full order-0 lg:order-1">
              {isDesktop ? (
                <Grid totalNumberToDisplay={MAX_LAST_SEEN_PRODUCTS}>
                  {lastSeenProducts.map(product => (
                    <Product
                      key={product.id}
                      offer={product.offer}
                      onClick={() => track({ labelGA: product.id })}
                      {...product}
                    />
                  ))}
                  {[...Array(numberOfPlaceholders).keys()].map(pId => (
                    <LastSeenPlaceholder key={`lastSeenPlaceHolder-${pId}`} />
                  ))}
                </Grid>
              ) : (
                <Slider
                  className="w-screen -mx-5"
                  cols={0}
                  hasControls={false}
                  scrollBackwardLabel={t('common.scrollBackward')}
                  scrollForwardLabel={t('common.scrollForward')}
                  navPosition="center"
                  scrollAreaContainerClassName="overflow-x-auto"
                >
                  {[
                    ...lastSeenProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className={classnames('relative pr-5', {
                          'ml-5': index === 0,
                        })}
                        style={{ width: '85vw', maxWidth: '380px' }}
                      >
                        <Product
                          offer={product.offer}
                          onClick={() => track({ labelGA: product.id })}
                          {...product}
                        />
                      </div>
                    )),
                    ...(numberOfProducts < MAX_LAST_SEEN_PRODUCTS
                      ? [
                          <LastSeenPlaceholder
                            key="lastSeenPlaceHolder"
                            className="pr-5"
                            styles={{ width: '85vw', maxWidth: '380px' }}
                          />,
                        ]
                      : []),
                  ]}
                </Slider>
              )}
            </div>
          </div>
        )}
    </SiteContainer>
  );
};
